import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Input, Spinner } from "theme-ui";

import { getAssignableContacts, AssignableContact } from "api/LocationClientProfiles";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { PlusIcon, LocationIcon, SearchIcon } from "components/svg";
import { themevals } from "theme/themevals";

/**
 * MASTER_PLAN.md §3.9 work item #13 — Client Detail Contacts tab.
 *
 * Design reference: docs/maintenance-module/mockups/15-client-detail-contacts.svg.
 *
 * Two sections:
 *
 *   1) Account Contacts — the people at this client organization.
 *      Wired via GET /clients/:id/contacts (LocationClientProfiles
 *      Phase A + Session 36 phone extension). Returns child Clients,
 *      or the client itself when it has no children (individual
 *      customer edge case).
 *
 *   2) Site Contacts — per-Location on-site people, captured on the
 *      `mulch_map_contacts` join. Section renders a shape-accurate
 *      deferred state; the client-scoped rollup endpoint ships with
 *      the maintenance-module work.
 *
 * Layout follows the mockup: section header row, then a single white
 * card container with hairline dividers between rows.
 */
export default function ContactsTab({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const toast = useToast();

  const [contacts, setContacts] = useState<AssignableContact[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const rows = await getAssignableContacts(clientId);
      if (!cancelled) {
        setContacts(rows);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  /**
   * Backend behavior: when a client has no children, listAssignableContacts
   * returns the client itself. Detect that shape by matching the sole row's
   * id to clientId — no server-side flag needed.
   */
  const isIndividualCustomer =
    !!contacts && contacts.length === 1 && contacts[0].id === clientId;

  const showSearch = !!contacts && !isIndividualCustomer && contacts.length > 5;

  const filtered = useMemo(() => {
    if (!contacts || isIndividualCustomer) return [];
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const nameMatch = (c.name ?? "").toLowerCase().includes(q);
      const emailMatch = (c.contactEmail ?? "").toLowerCase().includes(q);
      const phoneMatch = (c.phone ?? "").toLowerCase().includes(q);
      return nameMatch || emailMatch || phoneMatch;
    });
  }, [contacts, search, isIndividualCustomer]);

  const copyEmail = async (email: string | null) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      toast.add("Email copied.", "var(--theme-ui-colors-success)");
    } catch {
      toast.add("Clipboard blocked.", "var(--theme-ui-colors-danger)");
    }
  };

  const totalCount = contacts?.length ?? 0;
  const visibleCount = filtered.length;

  const addContact = () => {
    /**
     * Pre-selecting the parent org on the create form isn't wired yet;
     * this is the honest handoff. When Overview redesign (§3.9 #13
     * Overview half) plumbs a parentId query param through create.tsx,
     * this becomes navigate(`/clients/create?parentId=${clientId}`).
     */
    navigate("/clients/create");
  };

  return (
    <Box>
      {/* ══ Account Contacts ══════════════════════════════════════ */}
      <SectionHeader
        title="Account contacts"
        subtitle={
          loading
            ? undefined
            : isIndividualCustomer
              ? "individual customer"
              : totalCount > 0
                ? `${totalCount} · people at this organization`
                : "no contacts yet"
        }
        action={
          <BasicButton
            type="button"
            scheme="primary"
            onClick={addContact}
            $iconHeight="10px"
            $iconWidth="10px"
          >
            <PlusIcon fill="#fff" />
            Add contact
          </BasicButton>
        }
      />

      {loading ? (
        <Box sx={{ textAlign: "center", paddingY: "40px" }}>
          <Spinner />
        </Box>
      ) : isIndividualCustomer ? (
        <Card>
          <Box sx={{ padding: "24px" }}>
            <P color={themevals.colors.dkgrey} marginTop={0} marginBottom={0} fontSize={"13px"}>
              This is an individual customer, so there are no separate team contacts to manage. Additional contacts can be added by creating child records under this client.
            </P>
          </Box>
        </Card>
      ) : totalCount === 0 ? (
        <Card>
          <Box sx={{ padding: "24px" }}>
            <P color={themevals.colors.dkgrey} marginTop={0} marginBottom={0} fontSize={"13px"}>
              No account contacts yet. Use <Span fontWeight={600}>+ Add contact</Span> to create a person at this organization.
            </P>
          </Box>
        </Card>
      ) : (
        <>
          {showSearch && (
            <Flex sx={{ marginBottom: "12px", alignItems: "center", gap: "10px" }}>
              <Box sx={{ position: "relative", maxWidth: "320px", flex: 1 }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    pointerEvents: "none",
                    opacity: 0.55,
                  }}
                >
                  <SearchIcon fill={themevals.colors.dkgrey} height="12px" />
                </Box>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, or phone"
                  sx={{
                    fontSize: "13px",
                    fontFamily: "Poppins",
                    paddingLeft: "30px",
                    borderColor: "inputOutline",
                  }}
                />
              </Box>
              {search.trim() && (
                <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0}>
                  {visibleCount} of {totalCount}
                </Subtext>
              )}
            </Flex>
          )}

          <Card>
            {filtered.length === 0 ? (
              <Box sx={{ padding: "40px", textAlign: "center" }}>
                <Subtext fontSize={"13px"} fontFamily={"Poppins"}>
                  No contacts match {search.trim()}.
                </Subtext>
              </Box>
            ) : (
              filtered.map((c, idx) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  isLast={idx === filtered.length - 1}
                  onCopy={() => copyEmail(c.contactEmail)}
                  onView={() => navigate(`/clients/${c.id}`)}
                />
              ))
            )}
          </Card>
          {/* SESSION 45 GAP-49 — closing annotation from mockup 15
           * about email dedup + role history. Half of it (the role
           * history drill-down) isn't built; the dedup half is a real
           * backend behavior worth surfacing so staff know the same
           * person on ten sites still shows once. */}
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            marginTop={"10px"}
            color={themevals.colors.dkgrey}
          >
            Contacts on the same person across sites are deduped by email.
          </Subtext>
        </>
      )}

      {/* ══ Site Contacts ═════════════════════════════════════════ */}
      <Box sx={{ marginTop: "40px" }}>
        <SectionHeader
          title="Site contacts"
          subtitle="grouped by Location · captured on each site's map version"
        />

        <Card>
          {/* Table header shape — matches how it will render when the
              client-scoped mulch_map_contacts rollup endpoint ships
              with the maintenance module. */}
          <Flex
            sx={{
              padding: "14px 20px",
              borderBottom: "1px solid",
              borderColor: "inputOutline",
              background: "#FAFAF8",
              gap: "20px",
              alignItems: "center",
            }}
          >
            <Box sx={{ flex: "0 0 30%" }}>
              <Subtext
                fontSize={"11px"}
                fontFamily={"Poppins"}
                fontWeight={600}
                margin={0}
                color={themevals.colors.dkgrey}
              >
                SITE
              </Subtext>
            </Box>
            <Box sx={{ flex: "0 0 22%" }}>
              <Subtext
                fontSize={"11px"}
                fontFamily={"Poppins"}
                fontWeight={600}
                margin={0}
                color={themevals.colors.dkgrey}
              >
                CONTACT
              </Subtext>
            </Box>
            <Box sx={{ flex: "0 0 18%" }}>
              <Subtext
                fontSize={"11px"}
                fontFamily={"Poppins"}
                fontWeight={600}
                margin={0}
                color={themevals.colors.dkgrey}
              >
                ROLE
              </Subtext>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Subtext
                fontSize={"11px"}
                fontFamily={"Poppins"}
                fontWeight={600}
                margin={0}
                color={themevals.colors.dkgrey}
              >
                PHONE / EMAIL
              </Subtext>
            </Box>
          </Flex>

          {/* Empty state that describes the shape */}
          <Box sx={{ padding: "48px 20px", textAlign: "center" }}>
            <Box
              sx={{
                display: "inline-flex",
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                background: "#F1EFE8",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "14px",
              }}
            >
              <LocationIcon fill={themevals.colors.dkgrey} height="20px" />
            </Box>
            <P
              color={themevals.colors.black}
              marginTop={0}
              marginBottom={"6px"}
              fontSize={"13px"}
              fontWeight={600}
            >
              Site contacts land with the maintenance module
            </P>
            <Box sx={{ maxWidth: "480px", marginX: "auto" }}>
              <P
                color={themevals.colors.dkgrey}
                marginTop={0}
                marginBottom={0}
                fontSize={"12px"}
                lineHeight={"18px"}
              >
                Per-location contacts (facility manager, on-site director) are
                already stored on <Span fontFamily={"Menlo,monospace"} fontSize={"11px"}>mulch_map_contacts</Span>.
                A client-scoped rollup ships with the maintenance module.
                Until then, per-site contacts remain on each site{"'"}s map
                version where they were captured.
              </P>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}

/**
 * Section header row — title (H5), muted subtitle (dot-separated), and
 * an optional right-aligned action.
 */
function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <Flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "14px",
        flexWrap: "wrap",
        gap: "10px",
      }}
    >
      <Flex sx={{ alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
        <H5
          fontFamily="Poppins"
          fontWeight={600}
          margin={0}
          padding={0}
          color={themevals.colors.black}
          fontSize={"15px"}
        >
          {title}
        </H5>
        {subtitle && (
          <Subtext
            fontSize={"12px"}
            fontFamily={"Poppins"}
            margin={0}
            color={themevals.colors.dkgrey}
          >
            {subtitle}
          </Subtext>
        )}
      </Flex>
      {action}
    </Flex>
  );
}

/**
 * White rounded card wrapper.
 */
function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        background: "white",
        border: "1px solid",
        borderColor: "inputOutline",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
      }}
    >
      {children}
    </Box>
  );
}

/**
 * A single contact row inside the account-contacts card.
 */
function ContactRow({
  contact,
  isLast,
  onCopy,
  onView,
}: {
  contact: AssignableContact;
  isLast: boolean;
  onCopy: () => void;
  onView: () => void;
}) {
  const initials = getInitials(contact.name);
  const hasEmail = !!contact.contactEmail;
  const hasPhone = !!contact.phone;

  return (
    <Flex
      sx={{
        alignItems: "center",
        gap: "16px",
        padding: "16px 20px",
        borderBottom: isLast ? "none" : "1px solid",
        borderColor: "inputOutline",
        transition: "background 0.12s ease",
        "&:hover": { background: "#FAFAF8" },
      }}
    >
      {/* Avatar */}
      <Flex
        sx={{
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          background: "#F1EFE8",
          color: themevals.colors.dkgrey,
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Poppins",
          fontSize: "12px",
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {initials}
      </Flex>

      {/* Name + contact info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ marginBottom: "3px" }}>
          <Span
            fontFamily={"Poppins"}
            fontWeight={600}
            fontSize={"13px"}
            color={themevals.colors.black}
          >
            {contact.name || "Unnamed"}
          </Span>
        </Box>
        {(hasEmail || hasPhone) && (
          <Flex sx={{ gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            {hasEmail && (
              <Span
                fontFamily={"Poppins"}
                fontSize={"12px"}
                color={themevals.colors.dkgrey}
              >
                {contact.contactEmail}
              </Span>
            )}
            {hasEmail && hasPhone && (
              <Span
                fontFamily={"Poppins"}
                fontSize={"12px"}
                color={themevals.colors.dkgrey}
              >
                ·
              </Span>
            )}
            {hasPhone && (
              <Span
                fontFamily={"Poppins"}
                fontSize={"12px"}
                color={themevals.colors.dkgrey}
              >
                {contact.phone}
              </Span>
            )}
          </Flex>
        )}
      </Box>

      {/* Actions */}
      <Flex sx={{ gap: "18px", flexShrink: 0, alignItems: "center" }}>
        {hasEmail && (
          <Box
            as="button"
            onClick={onCopy}
            sx={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: 500,
              color: themevals.colors.dkgrey,
              transition: "color 0.12s ease",
              "&:hover": {
                color: themevals.colors.black,
                textDecoration: "underline",
              },
            }}
            aria-label={`Copy ${contact.contactEmail}`}
          >
            Copy email
          </Box>
        )}
        <Box
          as="button"
          onClick={onView}
          sx={{
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            fontFamily: "Poppins",
            fontSize: "12px",
            fontWeight: 500,
            color: themevals.colors.info,
            transition: "text-decoration 0.12s ease",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          View
        </Box>
      </Flex>
    </Flex>
  );
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
